/**
 * Admin Partner Brands CRUD (Phase 3.6).
 *
 * Lists every partner brand with quick toggle of active/inactive, plus a
 * dialog form for adding new brands and editing existing ones.
 */
import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = ['food', 'hygiene', 'vitamins', 'accessories', 'specials', 'other'];
const PET_TYPES = ['dog', 'cat', 'bird', 'fish', 'rodent', 'reptile', 'other'];

async function adminCall(path, opts = {}) {
  const t = getToken();
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
      Authorization: t ? `Bearer ${t}` : '',
    },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

const blank = {
  name: '', name_ka: '', category: 'food', accent: '#0A4D8C',
  logo_url: '', website_url: '', description: '',
  pet_types: ['dog'], sort_order: 100, active: true, show_on_home: false,
};

export default function AdminPartnerBrandsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, ...form}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminCall('/api/admin/partner-brands');
      setItems(d.items || []);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openCreate = () => setEditing({ ...blank });
  const openEdit = (row) => setEditing({ ...blank, ...row });

  const save = async () => {
    if (!editing) return;
    setSaving(true); setError('');
    try {
      const payload = { ...editing };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      payload.sort_order = Number(payload.sort_order) || 0;
      if (editing.id) {
        await adminCall(`/api/admin/partner-brands/${editing.id}`, {
          method: 'PUT', body: JSON.stringify(payload),
        });
      } else {
        await adminCall('/api/admin/partner-brands', {
          method: 'POST', body: JSON.stringify(payload),
        });
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this brand?')) return;
    await adminCall(`/api/admin/partner-brands/${id}`, { method: 'DELETE' });
    await load();
  };

  const togglePet = (pet) => {
    setEditing((cur) => {
      const has = cur.pet_types.includes(pet);
      return { ...cur, pet_types: has ? cur.pet_types.filter((p) => p !== pet) : [...cur.pet_types, pet] };
    });
  };

  // ---- Bulk sync (paste a JSON dump from another environment) ------------
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncText, setSyncText] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);

  const loadSyncSample = async () => {
    setSyncError(null);
    try {
      const r = await fetch('/downloads/partner_brands_sync.json', { cache: 'no-store' });
      if (!r.ok) throw new Error(`could not fetch sample (${r.status})`);
      const txt = await r.text();
      setSyncText(txt);
    } catch (e) {
      setSyncError(e.message);
    }
  };

  const runSync = async () => {
    setSyncBusy(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const parsed = JSON.parse(syncText);
      const res = await adminCall('/api/admin/partner-brands/sync', {
        method: 'POST',
        body: JSON.stringify(parsed),
      });
      setSyncResult(res);
      await load();
    } catch (e) {
      setSyncError(e.message);
    } finally {
      setSyncBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-partner-brands-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#05223D]">Partner Brands</h1>
          <p className="text-sm text-[#465B70] mt-1">Brands shown on the customer catalogue.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setSyncOpen(true); setSyncResult(null); setSyncError(null); }}
            className="rounded-xl"
            data-testid="brand-sync-button"
          >
            Sync from JSON
          </Button>
          <Button onClick={openCreate} className="rounded-xl bg-[#F25C05] hover:bg-[#d44e02]" data-testid="brand-add-button">
            <Plus className="mr-1.5 h-4 w-4" /> Add brand
          </Button>
        </div>
      </div>

      {error && <div className="rounded-xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8 px-3 py-2 text-sm text-[#9b1c1c]">{error}</div>}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-[#465B70]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-[#465B70]">No brands yet — add your first one.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F5F2EB] text-[#465B70]">
                <tr>
                  <th className="text-left px-4 py-2 w-8"></th>
                  <th className="text-left px-4 py-2">Brand</th>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-left px-4 py-2">Pets</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Home</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-t" data-testid={`brand-row-${row.id}`}>
                    <td className="px-4 py-2">
                      <span className="inline-block h-5 w-5 rounded" style={{ background: row.accent || '#0A4D8C' }} />
                    </td>
                    <td className="px-4 py-2">
                      <p className="font-bold text-[#05223D]">{row.name}</p>
                      {row.website_url && (
                        <a href={row.website_url} target="_blank" rel="noreferrer" className="text-xs text-[#0A4D8C] inline-flex items-center gap-1 hover:underline">
                          {row.website_url.replace(/^https?:\/\//, '')} <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-2 capitalize">{row.category}</td>
                    <td className="px-4 py-2 text-xs">{(row.pet_types || []).join(', ')}</td>
                    <td className="px-4 py-2">
                      <Badge className={`rounded-full ${row.active ? 'bg-[#A9B89A]/55 text-[#05223D]' : 'bg-[#465B70]/15 text-[#465B70]'}`}>
                        {row.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      {row.show_on_home ? (
                        <Badge className="rounded-full bg-[#F25C05]/15 text-[#F25C05] border border-[#F25C05]/30">On home</Badge>
                      ) : (
                        <span className="text-xs text-[#465B70]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(row)} className="rounded-lg" data-testid={`brand-edit-${row.id}`}>
                        <Edit3 className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => remove(row.id)} className="rounded-lg text-[#9b1c1c]" data-testid={`brand-delete-${row.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="brand-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing?.id ? 'Edit brand' : 'Add brand'}
            </DialogTitle>
            <DialogDescription>Customer catalogue picks this up automatically.</DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name (EN)</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-xl" data-testid="brand-form-name" />
                </div>
                <div>
                  <Label>Name (KA)</Label>
                  <Input value={editing.name_ka || ''} onChange={(e) => setEditing({ ...editing, name_ka: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Accent color</Label>
                  <Input type="color" value={editing.accent || '#0A4D8C'} onChange={(e) => setEditing({ ...editing, accent: e.target.value })} className="h-10 rounded-xl" />
                </div>
              </div>

              <div>
                <Label>Website URL</Label>
                <Input value={editing.website_url || ''} onChange={(e) => setEditing({ ...editing, website_url: e.target.value })} placeholder="https://…" className="rounded-xl" />
              </div>

              <div>
                <Label>Logo URL (optional)</Label>
                <Input value={editing.logo_url || ''} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} placeholder="https://…/logo.png" className="rounded-xl" />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea rows={3} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-xl" />
              </div>

              <div>
                <Label>Pet types</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PET_TYPES.map((p) => {
                    const active = editing.pet_types.includes(p);
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => togglePet(p)}
                        className={`rounded-full px-3 py-1 text-xs capitalize border ${active ? 'bg-[#0A4D8C] text-white border-[#0A4D8C]' : 'border-[#0A4D8C]/30 text-[#465B70]'}`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <Label>Sort order</Label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} className="rounded-xl" />
                </div>
                <div className="flex items-center justify-between rounded-xl border px-3 py-2">
                  <span className="text-sm">Active</span>
                  <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="brand-form-active" />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-[#F25C05]/30 bg-[#F25C05]/8 px-3 py-2.5">
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-[#05223D]">Show on Home page strip</span>
                  <span className="text-xs text-[#465B70]">Brand logo will appear in the homepage SMARTPAW-ს პარტნიორი ბრენდები marquee.</span>
                </div>
                <Switch
                  checked={!!editing.show_on_home}
                  onCheckedChange={(v) => setEditing({ ...editing, show_on_home: v })}
                  data-testid="brand-form-show-on-home"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-xl bg-[#F25C05] hover:bg-[#d44e02]" data-testid="brand-form-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Bulk sync dialog ---------- */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="max-w-3xl" data-testid="brand-sync-dialog">
          <DialogHeader>
            <DialogTitle>Sync partner brands from JSON</DialogTitle>
            <DialogDescription>
              Paste a JSON payload exported from another environment (e.g. preview), or click
              <b> Load bundled sample </b> to use the file shipped with this deploy.
              The sync will upsert brands by name and (by default) clear <code>show_on_home</code> flags first.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={loadSyncSample}
                className="rounded-xl"
                data-testid="brand-sync-load-sample"
              >
                Load bundled sample (/downloads/partner_brands_sync.json)
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setSyncText(''); setSyncResult(null); setSyncError(null); }}
                className="rounded-xl"
              >
                Clear
              </Button>
            </div>
            <Textarea
              value={syncText}
              onChange={(e) => setSyncText(e.target.value)}
              rows={14}
              placeholder='{ "mode": "replace", "reset_home_flags": true, "items": [ ... ] }'
              className="rounded-xl font-mono text-xs"
              data-testid="brand-sync-textarea"
            />
            {syncError && (
              <div className="rounded-xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8 px-3 py-2 text-sm text-[#9b1c1c]">
                {syncError}
              </div>
            )}
            {syncResult && (
              <div className="rounded-xl border border-[#0f6e44]/30 bg-[#0f6e44]/8 px-3 py-2 text-sm text-[#0f6e44]">
                <b>Sync complete.</b>{' '}
                Received {syncResult.received} · Created {syncResult.created} · Updated {syncResult.updated}
                {syncResult.deleted > 0 ? ` · Deleted ${syncResult.deleted}` : ''}
                {syncResult.home_flags_cleared > 0 ? ` · Home flags cleared ${syncResult.home_flags_cleared}` : ''}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSyncOpen(false)}
              className="rounded-xl"
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={runSync}
              disabled={syncBusy || !syncText.trim()}
              className="rounded-xl bg-[#F25C05] hover:bg-[#d44e02]"
              data-testid="brand-sync-run"
            >
              {syncBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sync now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
